// A side panel for the page editor (Version history, Share, Analytics, and the
// rail on phones): a dimmed overlay and a panel sliding in from the right or
// the left, 52px head with the title and a close button, a scrolling body.
// Full width on phones. Esc and an overlay click close it; Esc is marked as
// handled so the editor does not also clear the selection.
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, XIcon } from '../icons';

export function EditorDrawer({ open, onClose, title, side = 'right', width = 440, flush = false, children }: {
  open: boolean;
  onClose: () => void;
  title: string;
  side?: 'left' | 'right';
  /** Panel width in px on wider screens (capped at the screen width). */
  width?: number;
  /** No body padding (the rail brings its own). */
  flush?: boolean;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    if (!open) return;
    const before = document.activeElement as HTMLElement | null;
    panelRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (before?.isConnected && before !== document.body) before.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;
  // The wrapper carries pe-scope: the panel lives on document.body, outside
  // the editor root, and still needs the editor's styles.
  return createPortal(
    <div className="pe-scope">
      <div className="pe-overlay" onClick={onClose} aria-hidden="true" />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}
        className={`pe-drawer ${side === 'left' ? 'pe-drawer--left' : ''}`}
        onKeyDown={(e) => {
          if (e.key !== 'Tab' || e.defaultPrevented || !e.currentTarget.contains(e.target as Node)) return;
          const controls = [...e.currentTarget.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex]')]
            .filter((el) => el.tabIndex >= 0 && !el.matches(':disabled') && !el.closest('[hidden], [inert]')
              && getComputedStyle(el).display !== 'none' && getComputedStyle(el).visibility !== 'hidden');
          const first = controls[0];
          const last = controls.at(-1);
          if (!first) { e.preventDefault(); e.currentTarget.focus(); return; }
          if (e.target === e.currentTarget || (e.shiftKey ? e.target === first : e.target === last)) {
            e.preventDefault();
            (e.shiftKey ? last : first)?.focus();
          }
        }}
        style={{ width: `min(${width}px, 100vw)` }}>
        <div className="pe-drawer-head">
          <h2>{title}</h2>
          <button type="button" className="pe-icon-btn" aria-label="Close" onClick={onClose}>
            {side === 'left' ? <ChevronDownIcon size={16} className="rotate-90" /> : <XIcon size={16} />}
          </button>
        </div>
        <div className={`pe-drawer-body ${flush ? 'pe-drawer-body--flush' : ''}`}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
